import React, { useEffect, useState } from 'react';
import api from '../api';
import DOMPurify from 'dompurify';
import TagSelector from './TagSelector';
import { AuthImg, openAuthedFile } from './AuthImg';

interface SearchResult {
  merchant_name?: string;
  transaction_date?: string;
  total_amount?: number;
  currency?: string;
  client_reference?: string;
  minio_path?: string;
  thumbnail_path?: string;
  doc_id?: number;
  tags?: string[];
  _highlight?: {
    xml_content?: string[];
    merchant_name?: string[];
  };
}

interface SearchTabProps {
  onViewAnalysis?: (id: number) => void;
}

const SearchTab: React.FC<SearchTabProps> = ({ onViewAnalysis }) => {
  const [query, setQuery] = useState('');
  const [mode, setMode] = useState<'keyword' | 'semantic'>('keyword');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [selectedTags, setSelectedTags] = useState<string[]>([]);
  const [availableTags, setAvailableTags] = useState<string[]>([]);
  const [results, setResults] = useState<SearchResult[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    api.get('/tags').then((r) => setAvailableTags(r.data)).catch(() => {});
  }, []);

  // Core search — accepts an optional mode override so the toggle can pass the
  // new value before React has flushed the setMode state update.
  const executeSearch = async (searchMode: 'keyword' | 'semantic' = mode) => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (query) params.append('q', query);
      params.append('mode', searchMode);
      if (startDate) params.append('start_date', startDate);
      if (endDate) params.append('end_date', endDate);
      if (selectedTags.length > 0) params.append('tags', selectedTags.join(','));

      const res = await api.get(`/search?${params.toString()}`);
      setResults(res.data);
    } catch (err) {
      console.error('Suche fehlgeschlagen', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    executeSearch();
  };

  const handleDateKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      executeSearch();
    }
  };

  const hasActiveFilters = startDate || endDate || selectedTags.length > 0;

  return (
    <div className="w-full max-w-6xl">
      <form onSubmit={handleSearch} className="mb-6 space-y-3">
        {/* Suchleiste */}
        <div className="flex gap-2">
          <input
            type="text"
            placeholder="Suche nach Händler, Inhalt oder Referenz..."
            className="flex-1 p-4 rounded-lg border border-gray-300 focus:border-blue-500 outline-none shadow-sm transition-all"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
          <button
            type="submit"
            className="bg-blue-600 text-white px-8 py-4 rounded-lg font-bold hover:bg-blue-700 transition shadow-md"
          >
            {loading ? 'Suche...' : 'Suchen'}
          </button>
        </div>

        {/* Filter-Zeile */}
        <div className="flex flex-wrap gap-4 items-center">
          {/* Modus-Toggle */}
          <div className="flex items-center gap-1 bg-gray-100 rounded-lg p-1">
            <button
              type="button"
              onClick={() => { setMode('keyword'); executeSearch('keyword'); }}
              className={`px-4 py-1.5 rounded-md text-sm font-medium transition ${
                mode === 'keyword'
                  ? 'bg-white shadow text-blue-700'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              Keyword
            </button>
            <button
              type="button"
              onClick={() => { setMode('semantic'); executeSearch('semantic'); }}
              className={`px-4 py-1.5 rounded-md text-sm font-medium transition ${
                mode === 'semantic'
                  ? 'bg-white shadow text-blue-700'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              Semantisch
            </button>
          </div>

          {/* Datumsbereich */}
          <div className="flex items-center gap-2">
            <label className="text-sm text-gray-500 whitespace-nowrap">Von:</label>
            <input
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              onKeyDown={handleDateKeyDown}
              className="p-2 rounded-lg border border-gray-300 text-sm focus:border-blue-500 outline-none"
            />
          </div>
          <div className="flex items-center gap-2">
            <label className="text-sm text-gray-500 whitespace-nowrap">Bis:</label>
            <input
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              onKeyDown={handleDateKeyDown}
              className="p-2 rounded-lg border border-gray-300 text-sm focus:border-blue-500 outline-none"
            />
          </div>
          {/* Tags-Filter */}
          <div className="flex items-center gap-2 flex-1 min-w-[220px]">
            <label className="text-sm text-gray-500 whitespace-nowrap">Tags:</label>
            <TagSelector
              selectedTags={selectedTags}
              onChange={setSelectedTags}
              availableTags={availableTags}
              placeholder="Nach Tags filtern..."
              className="flex-1"
            />
          </div>
          {hasActiveFilters && (
            <button
              type="button"
              onClick={() => { setStartDate(''); setEndDate(''); setSelectedTags([]); }}
              className="text-xs text-gray-400 hover:text-gray-600 underline"
            >
              Filter zurücksetzen
            </button>
          )}
        </div>
      </form>

      {/* Ergebnisse */}
      <div className="space-y-4">
        {results.length === 0 && !loading && (query || hasActiveFilters) && (
          <p className="text-center text-gray-400 py-10">Keine Treffer gefunden.</p>
        )}

        {results.map((doc, idx) => {
          const highlights = [
            ...(doc._highlight?.xml_content ?? []),
            ...(doc._highlight?.merchant_name ?? []),
          ];

          return (
            <div
              key={idx}
              className="bg-white rounded-lg border border-gray-200 shadow-sm hover:shadow-md transition overflow-hidden"
            >
              <div className="flex gap-4 p-5">
                {/* Thumbnail */}
                {doc.thumbnail_path && (
                  <button
                    onClick={() => openAuthedFile(`/get_file?path=${encodeURIComponent(doc.minio_path ?? '')}`)}
                    className="flex-shrink-0"
                  >
                    <AuthImg
                      apiPath={`/get_file?path=${encodeURIComponent(doc.thumbnail_path)}`}
                      alt="Vorschau"
                      className="w-16 h-20 object-cover rounded border border-gray-200 shadow-sm hover:opacity-90 transition"
                    />
                  </button>
                )}

                <div className="flex-1 min-w-0">
                  <div className="flex justify-between items-start gap-4">
                    <div className="min-w-0">
                      <h3 className="text-base font-bold text-gray-800 truncate">
                        {doc.merchant_name || 'Unbekannter Händler'}
                      </h3>
                      <p className="text-sm text-gray-500 mt-0.5">
                        {doc.transaction_date && <>Datum: {doc.transaction_date} · </>}
                        Betrag:{' '}
                        <span className="font-semibold text-gray-700">
                          {doc.total_amount?.toFixed(2)} {doc.currency}
                        </span>
                      </p>
                      {doc.client_reference && (
                        <span className="mt-1.5 inline-block bg-blue-50 text-blue-700 text-[10px] px-2 py-0.5 rounded uppercase font-bold tracking-wider">
                          Ref: {doc.client_reference}
                        </span>
                      )}
                      {doc.tags && doc.tags.length > 0 && (
                        <div className="flex flex-wrap gap-1 mt-1.5">
                          {doc.tags.map((tag) => (
                            <span key={tag} className="bg-orange-100 text-orange-700 text-[10px] px-2 py-0.5 rounded-full font-medium">
                              {tag}
                            </span>
                          ))}
                        </div>
                      )}
                    </div>

                    {/* Aktions-Buttons */}
                    <div className="flex flex-col gap-1.5 flex-shrink-0">
                      <button
                        onClick={() => openAuthedFile(`/get_file?path=${encodeURIComponent(doc.minio_path ?? '')}`)}
                        className="text-blue-600 text-xs font-medium border border-blue-200 px-3 py-1.5 rounded hover:bg-blue-50 transition text-center whitespace-nowrap"
                      >
                        Datei anzeigen ↗
                      </button>
                      {doc.doc_id != null && onViewAnalysis && (
                        <button
                          onClick={() => onViewAnalysis(doc.doc_id!)}
                          className="text-emerald-700 text-xs font-medium border border-emerald-200 px-3 py-1.5 rounded hover:bg-emerald-50 transition whitespace-nowrap"
                        >
                          Analyse anzeigen
                        </button>
                      )}
                    </div>
                  </div>

                  {/* Highlight-Snippets */}
                  {highlights.length > 0 && (
                    <div className="mt-3 space-y-1 border-t border-gray-100 pt-3">
                      {highlights.slice(0, 2).map((hl, hlIdx) => (
                        <p
                          key={hlIdx}
                          className="text-xs text-gray-500 leading-relaxed"
                          dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(hl, { ALLOWED_TAGS: ['mark'] }) }}
                        />
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* CSS für mark-Tags aus den Highlights */}
      <style>{`mark { background-color: #fef08a; padding: 0 2px; border-radius: 2px; font-weight: 600; color: inherit; }`}</style>
    </div>
  );
};

export default SearchTab;
